import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert, Badge, Button, Card, DatePicker, Divider, Drawer, Empty, Form,
    Input, InputNumber, Modal, Popconfirm, Popover, Select, Space, Spin,
    Switch, Tag, Tooltip, Typography, message, theme,
} from 'antd';
import { SaveFilled, CopyOutlined, AppstoreOutlined } from '@ant-design/icons';
import { FaPlay } from 'react-icons/fa6';
import { Responsive as ResponsiveGrid } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTranslation } from 'react-i18next';
import {
    listDataSources,
    getDataSourceById,
    listDataSourceSchemas,
    createDataSource,
    updateDataSource,
    deleteDataSource,
    listReportFunctions,
    getReportFunctionById,
    listReportFunctionTemplates,
    createReportFunction,
    updateReportFunction,
    deleteReportFunction,
    deployReportFunction,
    getReportFunctionDefinition,
    listPgFunctions,
    executeRawQuery,
    resetRawQuery,
    patchReportDashboard,
    executeReportFunction,
} from '../../../actions/reportingActions';
import {
    IconAdd, IconDatabase, IconCode, IconDelete, IconReload, IconEdit,
} from './reportingIcons';
import { applyFunctionTemplate, getTemplateSelectOptions } from './functionSqlTemplates';
import {
    isFunctionDdl,
    isReadOnlyQuery,
    extractSelectFromFunction,
    substituteFunctionParamsForPreview,
    buildCreateFunctionFromSelect,
    replaceFunctionNameInDdl,
    inferChartEncoding,
} from './reportingSqlUtils';
import ChartRenderer from './ChartRenderer';
import ReportingChartCard from './ReportingChartCard';
import AddChartDrawer from './AddChartDrawer';
import { buildChartExecuteFilter, buildChartFilterSignature, cloneChart, computeChartTotal, enrichVizSpec } from './reportingChartUtils';
import { isStatVizType } from './reportingStatCard';
import {
    processChartDataset,
    pickDatasetFromResult,
    sharedChartResultCache,
    clearSharedChartResultCache,
    runWithConcurrencyPool,
    chartExecuteCacheKey,
    CHART_FILTER_DEBOUNCE_MS,
} from './reportingChartCache';
import useCategoryLabelMaps from './useCategoryLabelMaps';
import {
    parseLayoutItems,
    isTextLayoutItem,
    isChartLayoutItem,
    createTextBlock,
    cloneTextBlock,
    toGridLayoutItem,
    mergeGridLayoutIntoItems,
    gridLayoutsEqual,
    layoutItemsEqual,
    gridLayoutDragProps,
} from './dashboardLayoutItems';
import { resolveChartFunctionId } from './dashboardPortability';
import AddLayoutPlaceholders from './AddLayoutPlaceholders';
import TextBlockModal from './TextBlockModal';
import ReportingTextBlockSection from './ReportingTextBlockSection';
import { ifAccessDenied } from '../../../utils/formatApiError';
import ApiErrorAlert from '../../common/ApiErrorAlert';
import {
    REPORTING_SSL_OPTIONS,
    connectionFieldsFromDataSource,
    buildConnectionStringFromForm,
    pickDefaultDataSourceId,
} from './reportingConnectionUtils';
const { Text } = Typography;
const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const SIDER_W = 300;

// ─── Section heading ──────────────────────────────────────────────────────────
function SectionHead({ label, action }) {
    const { token } = theme.useToken();
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
                textTransform: 'uppercase', color: token.colorTextSecondary,
            }}>
                {label}
            </Text>
            {action}
        </div>
    );
}

// ─── Catalog item row ─────────────────────────────────────────────────────────
function CatalogItem({ icon, name, meta, isActive, onClick, onEdit, onDelete, deleteConfirm }) {
    const { token } = theme.useToken();
    return (
        <div
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? e => e.key === 'Enter' && onClick() : undefined}
            style={{
                display: 'flex', alignItems: 'center', padding: '7px 10px',
                borderRadius: 6, cursor: onClick ? 'pointer' : 'default',
                marginBottom: 3, userSelect: 'none',
                background: isActive ? token.colorPrimaryBg : 'transparent',
                border: isActive ? `1px solid ${token.colorPrimaryBorder}` : '1px solid transparent',
                transition: 'background 0.15s',
                outline: 'none',
            }}
        >
            <span style={{
                color: isActive ? token.colorPrimary : token.colorTextSecondary,
                marginRight: 8, flexShrink: 0, fontSize: 14,
            }}>
                {icon}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                </div>
                {meta && (
                    <div style={{ fontSize: 12, color: token.colorTextSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {meta}
                    </div>
                )}
            </div>
            {(onEdit || onDelete) && (
                <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0, display: 'flex', gap: 2 }}>
                    {onEdit && (
                        <Tooltip title={onEdit.title}>
                            <Button size="small" type="text" icon={<IconEdit style={{ fontSize: 13 }} />} onClick={onEdit.onClick} />
                        </Tooltip>
                    )}
                    {onDelete && (
                        <Popconfirm title={deleteConfirm} onConfirm={onDelete} okType="danger">
                            <Button size="small" type="text" danger icon={<IconDelete style={{ fontSize: 13 }} />} />
                        </Popconfirm>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── SQL Lab Modal ────────────────────────────────────────────────────────────
function SqlLabModal({ open, onClose, activeDsId, activeSchema, dataSources, editingFunction, onFunctionCreated }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { token } = theme.useToken();

    const { items: registeredFunctions = [] } = useSelector(s => s.reportFunctionList);
    const { loading: deploying, error: deployError } = useSelector(s => s.reportFunctionDeploy);
    const { loading: queryRunning, result: queryResult, error: queryError } = useSelector(s => s.reportDataSourceQuery ?? {});
    const { schemas = [], loading: schemasLoading } = useSelector(s => s.reportDataSourceSchemas);
    const { templates = [], categories = {}, loading: templatesLoading } = useSelector(s => s.reportFunctionTemplates);

    // Shared state
    const [dsId, setDsId]       = useState(activeDsId);
    const [schema, setSchema]   = useState(activeSchema || 'public');
    const resultsRef = useRef(null);
    const [queryDatasetKey, setQueryDatasetKey] = useState('default');
    const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);

    const [selectedTemplate, setSelectedTemplate] = useState('dv_cosd_by_quality');
    const [fnSql, setFnSql]                 = useState('');
    const [fnSqlLoading, setFnSqlLoading]   = useState(false);
    const [fnSqlLoadError, setFnSqlLoadError] = useState(null);
    const [functionName, setFunctionName] = useState('aggregate_report');
    const [displayName, setDisplayName]     = useState('aggregate report');
    const [description, setDescription]     = useState('');
    const [saving, setSaving]               = useState(false);
    const [editingFnId, setEditingFnId]     = useState(null);

    const schemaOptions = schemas.length > 0 ? schemas.map(s => ({ value: s, label: s })) : [{ value: 'public', label: 'public' }];

    useEffect(() => {
        if (queryResult) {
            setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
        }
    }, [queryResult]);

    useEffect(() => {
        if (!open) return;
        dispatch(resetRawQuery());
        if (templates.length === 0) {
            dispatch(listReportFunctionTemplates());
        }

        let cancelled = false;

        const initForm = async () => {
            if (editingFunction) {
                const fnDs = editingFunction.dataSourceId ?? editingFunction.DataSourceId;
                const fnSchema = editingFunction.schema ?? editingFunction.Schema ?? 'public';
                const fnName = editingFunction.functionName ?? editingFunction.FunctionName ?? 'aggregate_report';
                setDsId(fnDs || activeDsId);
                setSchema(fnSchema);
                setFunctionName(fnName);
                setDisplayName(editingFunction.displayName ?? editingFunction.DisplayName ?? fnName.replace(/_/g, ' '));
                setDescription(editingFunction.description ?? '');
                setEditingFnId(editingFunction.id ?? editingFunction.Id ?? null);
                setFnSql('');
                setFnSqlLoadError(null);
                setFnSqlLoading(true);

                if (fnDs && fnName) {
                    const definition = await dispatch(getReportFunctionDefinition(fnDs, fnSchema, fnName));
                    if (cancelled) return;
                    if (ifAccessDenied(definition, t)) {
                        onClose();
                        return;
                    }
                    const ddl = definition?.definition ?? definition?.Definition;
                    if (ddl) {
                        setFnSql(ddl);
                    } else {
                        const loadedTemplates = templates.length > 0
                            ? templates
                            : await dispatch(listReportFunctionTemplates());
                        setFnSqlLoadError(definition?.error ?? t('Could not load function SQL from the database.'));
                        setFnSql(applyFunctionTemplate('dv_cosd_by_quality', fnSchema, fnName, loadedTemplates ?? []));
                    }
                }
                setFnSqlLoading(false);
            } else {
                const defaultDs = activeDsId ?? pickDefaultDataSourceId(dataSources);
                setDsId(defaultDs);
                setSchema(activeSchema || 'public');
                setDescription('');
                setEditingFnId(null);
                setFnSqlLoadError(null);
                setFnSqlLoading(false);
            }
        };

        initForm();

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingFunction, dataSources.length]);

    useEffect(() => {
        if (!open || editingFunction || templates.length === 0) return;
        applyFnTemplate(selectedTemplate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, templates.length, editingFunction]);

    useEffect(() => {
        if (dsId) dispatch(listDataSourceSchemas(dsId));
    }, [dsId, dispatch]);

    const applyFnTemplate = (key) => {
        setSelectedTemplate(key);
        const tpl = templates.find(x => (x.key ?? x.Key) === key);
        const fnName = tpl?.defaultFunctionName || 'my_report_fn';
        setFunctionName(fnName);
        setDisplayName(fnName.replace(/_/g, ' '));
        setFnSql(applyFunctionTemplate(key, schema, fnName, templates));
        setTemplatePopoverOpen(false);
    };

    const selectedTplMeta = templates.find(x => (x.key ?? x.Key) === selectedTemplate);

    /** Run the inner SELECT from the function body without deploying (preview before deploy). */
    const handlePreviewFunction = async () => {
        if (!dsId || !fnSql.trim()) return;
        let sql = fnSql.trim();
        if (!isFunctionDdl(sql)) {
            if (isReadOnlyQuery(sql)) {
                sql = buildCreateFunctionFromSelect(sql, schema, functionName || 'aggregate_report');
            } else {
                message.error(t('Enter CREATE FUNCTION SQL, or a SELECT to preview.'));
                return;
            }
        }
        const select = extractSelectFromFunction(sql);
        if (!select) {
            message.error(t(
                'Could not find a SELECT after RETURN QUERY. Use the template shape: RETURN QUERY then SELECT … END; — do not put CREATE FUNCTION inside RETURN QUERY.'
            ));
            return;
        }
        dispatch(resetRawQuery());
        const result = await dispatch(executeRawQuery(dsId, substituteFunctionParamsForPreview(select), 500));
        if (result?.datasets) {
            const keys = Object.keys(result.datasets);
            setQueryDatasetKey(keys[0] ?? 'default');
        }
    };

    const handleFunctionNameChange = (name) => {
        const safe = (name || '').replace(/[^\w]/g, '_');
        setFunctionName(safe);
        if (isFunctionDdl(fnSql)) {
            setFnSql(replaceFunctionNameInDdl(fnSql, schema, safe));
        }
    };

    /** Deploy to Postgres and register in one step (required for Add chart). */
    const handleSaveFunction = async () => {
        const fnName = (functionName || '').trim().replace(/[^\w]/g, '_');
        const label = displayName.trim();
        if (!dsId || !fnSql.trim()) return;
        if (!fnName) {
            message.error(t('Function name is required'));
            return;
        }
        if (!label) {
            message.error(t('Display name is required'));
            return;
        }

        let sql = fnSql.trim();
        if (!isFunctionDdl(sql)) {
            if (isReadOnlyQuery(sql)) {
                sql = buildCreateFunctionFromSelect(sql, schema, fnName);
            } else {
                message.error(t('Paste CREATE FUNCTION SQL or a SELECT to wrap.'));
                return;
            }
        } else {
            sql = replaceFunctionNameInDdl(sql, schema, fnName);
        }
        setFnSql(sql);

        setSaving(true);
        const deployResult = await dispatch(deployReportFunction(dsId, sql));
        if (!deployResult) {
            setSaving(false);
            return;
        }

        const schemaName = deployResult.schema || schema || 'public';
        const resolvedFnName = deployResult.functionName || fnName;
        const dto = {
            dataSourceId: dsId,
            schema: schemaName,
            functionName: resolvedFnName,
            displayName: label,
            description: description?.trim() || null,
            enabled: true,
        };

        const existingId = editingFnId
            ?? registeredFunctions.find(f => {
                const fDs = f.dataSourceId ?? f.DataSourceId;
                const fSchema = f.schema ?? f.Schema;
                const fName = f.functionName ?? f.FunctionName;
                return fDs === dsId && fSchema === schemaName && fName === resolvedFnName;
            })?.id;
        const registerResult = existingId
            ? await dispatch(updateReportFunction(existingId, { ...dto, id: existingId }))
            : await dispatch(createReportFunction(dto));

        setSaving(false);

        if (registerResult?.error) {
            message.error(registerResult.error);
            return;
        }
        if (registerResult) {
            message.success(t('Function saved — available in Add chart'));
            setFunctionName(resolvedFnName);
            dispatch(listPgFunctions(dsId, schemaName));
            onFunctionCreated?.();
        }
    };

    const canSaveFunction = Boolean(dsId && fnSql.trim() && functionName.trim() && displayName.trim());
    const queryDatasets = queryResult?.datasets ?? {};
    const currentQueryDataset = queryDatasets[queryDatasetKey];

    const labHeader = (
        <Space wrap size={8} style={{ marginBottom: 10 }}>
            <Space size={6} align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>{t('Database')}</Text>
                <Select
                    size="small"
                    style={{ minWidth: 160 }}
                    value={dsId}
                    onChange={v => { setDsId(v); dispatch(resetRawQuery()); }}
                    options={dataSources.filter(d => d.enabled !== false).map(d => ({ value: d.id, label: d.name }))}
                    placeholder={t('Select database')}
                />
            </Space>
            <Space size={6} align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>{t('Schema')}</Text>
                <Select
                    size="small"
                    style={{ minWidth: 100 }}
                    value={schema}
                    onChange={v => { setSchema(v); dispatch(resetRawQuery()); }}
                    options={schemaOptions}
                    loading={schemasLoading}
                />
            </Space>
        </Space>
    );

    const functionTabFooter = (
        <Space wrap size="middle" style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
                loading={queryRunning}
                disabled={!dsId || !fnSql.trim()}
                icon={<FaPlay />}
                onClick={handlePreviewFunction}
            >
                {t('Preview data')}
            </Button>
            <Button
                type="primary"
                icon={<SaveFilled />}
                loading={deploying || saving}
                disabled={!canSaveFunction}
                onClick={handleSaveFunction}
            >
                {t('Save function')}
            </Button>
        </Space>
    );

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={(
                <Space>
                    <IconCode />
                    {editingFunction ? t('Edit function') : t('SQL Lab')}
                </Space>
            )}
            width={900}
            footer={functionTabFooter}
            destroyOnClose
            styles={{
                body: {
                    maxHeight: 'min(62vh, 600px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: 16,
                },
            }}
        >
            <div style={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                background: token.colorBgElevated,
                marginBottom: 14,
            }}
            >
                {labHeader}

                <div style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-end',
                    marginBottom: 10,
                    flexWrap: 'wrap',
                }}
                >
                    <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Function name')} *</Text>
                        <Input
                            size="small"
                            style={{ marginTop: 4, fontFamily: 'monospace' }}
                            value={functionName}
                            onChange={e => handleFunctionNameChange(e.target.value)}
                            placeholder="dv_cosd_by_quality"
                        />
                    </div>
                    <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Display name')} *</Text>
                        <Input
                            size="small"
                            style={{ marginTop: 4 }}
                            value={displayName}
                            onChange={e => setDisplayName(e.target.value)}
                            placeholder={t('e.g. COSD counts by quality')}
                        />
                    </div>
                    <Space size={6} style={{ marginLeft: 'auto', paddingBottom: 2 }}>
                        <Popover
                            open={templatePopoverOpen}
                            onOpenChange={setTemplatePopoverOpen}
                            trigger="click"
                            placement="bottomRight"
                            title={t('SQL template')}
                            content={(
                                <div style={{ width: 300 }}>
                                    <Select
                                        size="small"
                                        showSearch
                                        optionFilterProp="label"
                                        style={{ width: '100%' }}
                                        value={selectedTemplate}
                                        onChange={applyFnTemplate}
                                        loading={templatesLoading}
                                        options={getTemplateSelectOptions(templates, categories)}
                                    />
                                    {selectedTplMeta?.description && (
                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                                            {selectedTplMeta.description}
                                        </Text>
                                    )}
                                </div>
                            )}
                        >
                            <Button size="small" icon={<AppstoreOutlined />}>
                                {t('Template')}
                            </Button>
                        </Popover>
                    </Space>
                </div>
            </div>

            {fnSqlLoadError && (
                <Alert type="warning" message={fnSqlLoadError} showIcon style={{ marginBottom: 10 }} />
            )}

            <Spin spinning={fnSqlLoading}>
                <Input.TextArea
                        value={fnSql}
                        onChange={e => setFnSql(e.target.value)}
                        rows={16}
                        style={{ fontFamily: 'monospace', fontSize: 12, background: token.colorFillQuaternary, marginBottom: 10, resize: 'vertical' }}
                        placeholder="CREATE OR REPLACE FUNCTION ..."
                    />
            </Spin>

                    <ApiErrorAlert error={deployError} />
                    <ApiErrorAlert error={queryError} />
                    {(queryRunning || queryResult) && (
                        <div ref={resultsRef} style={{
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: 8,
                            padding: 8,
                            background: token.colorBgContainer,
                            minHeight: 180,
                            marginBottom: 10,
                        }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                                {t('Preview (inner SELECT)')}
                            </Text>
                            <ChartRenderer
                                dataset={currentQueryDataset}
                                vizSpec={{
                                    type: 'table',
                                    encoding: inferChartEncoding(currentQueryDataset?.columns ?? []),
                                }}
                                defaultMode="table"
                                title={t('Function preview')}
                                loading={queryRunning}
                            />
                        </div>
                    )}
        </Modal>
    );
}

// ─── Data Catalog Sidebar ─────────────────────────────────────────────────────
function DataCatalogSidebar({ activeDsId, setActiveDsId, activeSchema, setActiveSchema }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { token } = theme.useToken();

    const { items: dataSources = [], loading: dsLoading } = useSelector(s => s.reportDataSourceList);
    const { items: functions = [], loading: fnLoading } = useSelector(s => s.reportFunctionList);
    const { schemas = [], loading: schemasLoading } = useSelector(s => s.reportDataSourceSchemas);
    // Connection edit modal
    const [connModalOpen, setConnModalOpen] = useState(false);
    const [connForm] = Form.useForm();
    const [connSaving, setConnSaving] = useState(false);
    const [editingDs, setEditingDs] = useState(null);

    // SQL lab
    const [sqlLabOpen, setSqlLabOpen] = useState(false);
    const [editingFunction, setEditingFunction] = useState(null);

    const refreshDs = useCallback(() => dispatch(listDataSources()), [dispatch]);
    const refreshFn = useCallback(() => dispatch(listReportFunctions()), [dispatch]);

    useEffect(() => {
        refreshDs();
        refreshFn();
    }, [refreshDs, refreshFn]);

    useEffect(() => {
        if (activeDsId || !dataSources.length) return;
        const defaultId = pickDefaultDataSourceId(dataSources);
        if (defaultId) {
            setActiveDsId(defaultId);
            setActiveSchema('public');
        }
    }, [dataSources, activeDsId, setActiveDsId, setActiveSchema]);

    useEffect(() => {
        if (activeDsId) dispatch(listDataSourceSchemas(activeDsId));
    }, [activeDsId, dispatch]);

    const openAddConnection = () => {
        setEditingDs(null);
        connForm.resetFields();
        connForm.setFieldsValue({
            name: t('External database'),
            database: 'openep_core',
            host: 'localhost',
            sslMode: 'prefer',
            port: 5432,
            username: 'postgres',
            enabled: true,
        });
        setConnModalOpen(true);
    };

    const openEditConnection = async (ds) => {
        const detail = await dispatch(getDataSourceById(ds.id ?? ds.Id));
        if (ifAccessDenied(detail, t)) return;
        setEditingDs(detail);
        connForm.setFieldsValue(connectionFieldsFromDataSource(detail));
        setConnModalOpen(true);
    };

    const handleSaveConnection = async () => {
        let values;
        try { values = await connForm.validateFields(); } catch { return; }
        const isLegacyDefault = editingDs?.kind === 'default' && !editingDs?.connectionString;
        if (isLegacyDefault) {
            setConnSaving(true);
            const result = await dispatch(updateDataSource(editingDs.id, {
                id: editingDs.id,
                name: values.name,
                description: values.description,
                kind: 'default',
                enabled: values.enabled ?? true,
                connectionString: null,
            }));
            setConnSaving(false);
            if (ifAccessDenied(result, t)) return;
            if (result) {
                setConnModalOpen(false);
                setEditingDs(null);
                refreshDs();
                message.success(t('Connection updated'));
            }
            return;
        }
        setConnSaving(true);
        const connectionString = buildConnectionStringFromForm(values, editingDs?.connectionString);
        const dto = {
            name: values.name,
            description: values.description,
            kind: 'external',
            enabled: values.enabled ?? true,
            connectionString,
        };
        const result = editingDs
            ? await dispatch(updateDataSource(editingDs.id, { ...dto, id: editingDs.id }))
            : await dispatch(createDataSource(dto));
        setConnSaving(false);
        if (ifAccessDenied(result, t)) return;
        if (result) {
            setConnModalOpen(false);
            setEditingDs(null);
            refreshDs();
            message.success(editingDs ? t('Connection updated') : t('Connection added'));
        }
    };

    const openSqlLab = async (fn = null) => {
        if (fn) {
            const detail = await dispatch(getReportFunctionById(fn.id ?? fn.Id));
            if (ifAccessDenied(detail, t)) return;
            setEditingFunction(detail);
        } else {
            setEditingFunction(null);
        }
        setSqlLabOpen(true);
    };

    const closeSqlLab = () => {
        setSqlLabOpen(false);
        setEditingFunction(null);
    };

    const handleDeleteDs = async (id) => {
        const result = await dispatch(deleteDataSource(id));
        if (result !== true) {
            ifAccessDenied(result, t);
            return;
        }
        if (activeDsId === id) { setActiveDsId(null); setActiveSchema('public'); }
        refreshDs();
    };

    const handleDeleteFn = async (id) => {
        const result = await dispatch(deleteReportFunction(id));
        if (result !== true) {
            ifAccessDenied(result, t);
            return;
        }
        refreshFn();
    };

    const schemaOptions = schemas.length > 0
        ? schemas.map(s => ({ value: s, label: s }))
        : [{ value: 'public', label: 'public' }];

    const filteredFunctions = functions.filter(fn => {
        const fnDs = fn.dataSourceId ?? fn.DataSourceId;
        const fnSchema = fn.schema ?? fn.Schema ?? 'public';
        return (!activeDsId || fnDs === activeDsId)
            && (!activeSchema || fnSchema === activeSchema);
    });

    return (
        <div style={{ padding: '14px 12px' }}>
            {/* ── Databases ── */}
            <SectionHead label={t('Databases')} />

            <Button
                block
                icon={<IconAdd />}
                onClick={openAddConnection}
                style={{ marginBottom: 10 }}
            >
                {t('Add database')}
            </Button>

            {dsLoading ? (
                <Spin size="small" />
            ) : dataSources.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13, display: 'block', paddingLeft: 4 }}>
                    {t('No databases. Add a PostgreSQL connection (e.g. openep_core on localhost).')}
                </Text>
            ) : (
                dataSources.map(ds => (
                    <CatalogItem
                        key={ds.id}
                        icon={<IconDatabase />}
                        name={ds.name}
                        meta={ds.kind === 'external' ? t('External') : t('Default')}
                        isActive={activeDsId === ds.id}
                        onClick={() => { setActiveDsId(ds.id); setActiveSchema('public'); }}
                        onEdit={{ title: t('Edit connection'), onClick: () => openEditConnection(ds) }}
                        onDelete={() => handleDeleteDs(ds.id)}
                        deleteConfirm={t('Delete this database?')}
                    />
                ))
            )}

            {/* ── Schema ── */}
            {activeDsId && (
                <>
                    <Divider style={{ margin: '12px 0 10px' }} />
                    <SectionHead label={t('Schema')} />
                    <Select
                        size="large"
                        style={{ width: '100%' }}
                        value={activeSchema}
                        onChange={setActiveSchema}
                        options={schemaOptions}
                        loading={schemasLoading}
                    />
                </>
            )}

            {/* ── Functions ── */}
            {activeDsId && (
                <>
                    <Divider style={{ margin: '12px 0 10px' }} />
                    <SectionHead label={t('Functions')} />

                    <Button
                        size="large"
                        block
                        icon={<IconCode />}
                        onClick={() => openSqlLab()}
                        style={{ marginBottom: 10 }}
                    >
                        {t('SQL Lab — create function')}
                    </Button>

                    {fnLoading ? (
                        <Spin size="small" />
                    ) : filteredFunctions.length === 0 ? (
                        <Text type="secondary" style={{ fontSize: 13, display: 'block', paddingLeft: 4 }}>
                            {t('No functions yet. Use SQL Lab to create one.')}
                        </Text>
                    ) : (
                        filteredFunctions.map(fn => (
                            <CatalogItem
                                key={fn.id ?? fn.Id}
                                icon={<IconCode />}
                                name={fn.displayName ?? fn.DisplayName}
                                meta={`${fn.schema ?? fn.Schema}.${fn.functionName ?? fn.FunctionName}`}
                                isActive={false}
                                onEdit={{ title: t('Edit function'), onClick: () => openSqlLab(fn) }}
                                onDelete={() => handleDeleteFn(fn.id ?? fn.Id)}
                                deleteConfirm={t('Remove this function registration?')}
                            />
                        ))
                    )}
                </>
            )}

            <Modal
                open={connModalOpen}
                title={editingDs ? t('Edit database') : t('Add database')}
                onCancel={() => { setConnModalOpen(false); setEditingDs(null); }}
                onOk={handleSaveConnection}
                confirmLoading={connSaving}
                width={480}
                destroyOnClose
                okText={editingDs ? t('Save') : t('Add')}
            >
                <Form form={connForm} layout="vertical">
                    <Form.Item name="name" label={t('Display name')} rules={[{ required: true }]}>
                        <Input size="large" placeholder={t('e.g. External Prod DB')} />
                    </Form.Item>
                    <Form.Item name="description" label={t('Description')}>
                        <Input size="large" />
                    </Form.Item>
                    {editingDs?.kind === 'default' && !editingDs?.connectionString ? (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 10, fontSize: 12 }}
                            message={t('Legacy app-linked database — edit name and description only, or add a new connection with host/database.')}
                        />
                    ) : (
                        <>
                            <Divider style={{ margin: '4px 0 12px' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>{t('PostgreSQL connection')}</Text>
                            </Divider>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Form.Item
                                    name="host"
                                    label={t('Host')}
                                    rules={[{ required: true }]}
                                    style={{ display: 'inline-block', width: 'calc(68% - 8px)', marginRight: 8 }}
                                >
                                    <Input placeholder="localhost" />
                                </Form.Item>
                                <Form.Item name="port" label={t('Port')} style={{ display: 'inline-block', width: '32%' }}>
                                    <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="5432" />
                                </Form.Item>
                            </Form.Item>
                            <Form.Item name="database" label={t('Database')} rules={[{ required: true }]}>
                                <Input placeholder="openep_core" />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Form.Item
                                    name="username"
                                    label={t('Username')}
                                    style={{ display: 'inline-block', width: 'calc(50% - 8px)', marginRight: 8 }}
                                >
                                    <Input placeholder="postgres" />
                                </Form.Item>
                                <Form.Item name="password" label={t('Password')} style={{ display: 'inline-block', width: '50%' }}>
                                    <Input.Password placeholder={editingDs ? t('Leave blank to keep') : ''} />
                                </Form.Item>
                            </Form.Item>
                            <Form.Item name="sslMode" label={t('SSL mode')}>
                                <Select options={REPORTING_SSL_OPTIONS} />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="enabled" valuePropName="checked">
                        <Switch checkedChildren={t('Enabled')} unCheckedChildren={t('Disabled')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* ── SQL Lab Modal ── */}
            <SqlLabModal
                open={sqlLabOpen}
                onClose={closeSqlLab}
                activeDsId={activeDsId}
                activeSchema={activeSchema}
                dataSources={dataSources}
                editingFunction={editingFunction}
                onFunctionCreated={() => { refreshFn(); closeSqlLab(); }}
            />
        </div>
    );
}

// ─── DashboardEditorDrawer ────────────────────────────────────────────────────
const LAYOUT_AUTOSAVE_MS = 600;

export default function DashboardEditorDrawer({
    open,
    dashboard,
    applicationUserId,
    onClose,
    dateRange,
    globalFilters = {},
    onDashboardUpdated,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { token } = theme.useToken();
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(1200);

    const [activeDsId, setActiveDsId] = useState(null);
    const [activeSchema, setActiveSchema] = useState('public');

    const [localItems, setLocalItems] = useState([]);
    const [chartResults, setChartResults] = useState({});
    const [chartErrors, setChartErrors] = useState({});
    const [loadingIds, setLoadingIds] = useState(new Set());
    const [chartDrawerOpen, setChartDrawerOpen] = useState(false);
    const [chartToEdit, setChartToEdit] = useState(null);
    const [saving, setSaving] = useState(false);
    const [layoutSaving, setLayoutSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const layoutSaveTimer = useRef(null);
    const skipLayoutAutosaveRef = useRef(true);
    const localItemsRef = useRef([]);
    const [dashboardName, setDashboardName] = useState('');
    const [textModalOpen, setTextModalOpen] = useState(false);
    const [textToEdit, setTextToEdit] = useState(null);
    const { items: editorDataSources = [] } = useSelector(s => s.reportDataSourceList);
    const { items: registeredFunctions = [] } = useSelector(s => s.reportFunctionList);
    const categoryLabelMaps = useCategoryLabelMaps();

    useEffect(() => {
        if (open) {
            dispatch(listReportFunctions());
            dispatch(listDataSources());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (dashboard) setDashboardName(dashboard.name ?? '');
    }, [dashboard?.id, dashboard?.name]);

    useEffect(() => {
        if (!open || activeDsId || !editorDataSources.length) return;
        const defaultId = pickDefaultDataSourceId(editorDataSources);
        if (defaultId) {
            setActiveDsId(defaultId);
            setActiveSchema('public');
        }
    }, [open, activeDsId, editorDataSources]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let frameId = null;
        const ro = new ResizeObserver(entries => {
            if (frameId != null) cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(() => {
                frameId = null;
                for (const e of entries) {
                    const w = Math.round(e.contentRect.width);
                    setContainerWidth(prev => (prev === w ? prev : w));
                }
            });
        });
        ro.observe(el);
        return () => {
            if (frameId != null) cancelAnimationFrame(frameId);
            ro.disconnect();
        };
    }, []);

    const filterSignature = useMemo(
        () => buildChartFilterSignature(dateRange, globalFilters),
        [dateRange, globalFilters],
    );

    const [debouncedSig, setDebouncedSig] = useState(filterSignature);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSig(filterSignature), CHART_FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filterSignature]);

    const prevDebouncedSigRef = useRef(debouncedSig);
    useEffect(() => {
        if (prevDebouncedSigRef.current !== debouncedSig) {
            clearSharedChartResultCache();
            prevDebouncedSigRef.current = debouncedSig;
        }
    }, [debouncedSig]);

    const dateRangeRef = useRef(dateRange);
    const globalFiltersRef = useRef(globalFilters);
    useEffect(() => { dateRangeRef.current = dateRange; }, [dateRange]);
    useEffect(() => { globalFiltersRef.current = globalFilters; }, [globalFilters]);

    const clearChartState = useCallback((chartIds) => {
        setChartResults({});
        setChartErrors({});
        setLoadingIds(new Set(chartIds));
    }, []);

    const applyResultToChart = useCallback((chartDef, rawDataset) => {
        const dataset = processChartDataset(chartDef, rawDataset, categoryLabelMaps);
        setChartResults(s => ({ ...s, [chartDef.id]: dataset }));
        setChartErrors(s => ({ ...s, [chartDef.id]: null }));
    }, [categoryLabelMaps]);

    const chartItems = useMemo(() => localItems.filter(isChartLayoutItem), [localItems]);
    const chartItemCount = chartItems.length;

    const reloadAllCharts = useCallback(async () => {
        if (!open || chartItems.length === 0) {
            clearChartState([]);
            return;
        }

        clearChartState(chartItems.map(c => c.id));

        const filter = buildChartExecuteFilter({}, dateRangeRef.current, globalFiltersRef.current);
        const filterKey = buildChartFilterSignature(dateRangeRef.current, globalFiltersRef.current);
        const uniqueByFunction = new Map();
        for (const chart of chartItems) {
            const functionId = resolveChartFunctionId(chart, registeredFunctions, dashboard?.name);
            if (!functionId) continue;
            const key = chartExecuteCacheKey(functionId, filterKey);
            if (!uniqueByFunction.has(key)) {
                uniqueByFunction.set(key, { functionId, charts: [] });
            }
            uniqueByFunction.get(key).charts.push(chart);
        }

        const executeGroup = async (functionId, chartsForFn) => {
            const cacheKey = chartExecuteCacheKey(functionId, filterKey);
            let rawResult = sharedChartResultCache.get(cacheKey);

            if (!rawResult) {
                const result = await dispatch(executeReportFunction(functionId, filter));
                if (result?.error) {
                    for (const chart of chartsForFn) {
                        setChartErrors(s => ({ ...s, [chart.id]: result.error }));
                        setChartResults(s => ({ ...s, [chart.id]: null }));
                        setLoadingIds(s => { const n = new Set(s); n.delete(chart.id); return n; });
                    }
                    return;
                }
                rawResult = result;
                sharedChartResultCache.set(cacheKey, rawResult);
            }

            for (const chart of chartsForFn) {
                const rawDataset = pickDatasetFromResult(rawResult, chart.datasetKey);
                if (rawDataset) {
                    applyResultToChart(chart, rawDataset);
                } else {
                    setChartErrors(s => ({ ...s, [chart.id]: t('Error loading chart') }));
                    setChartResults(s => ({ ...s, [chart.id]: null }));
                }
                setLoadingIds(s => { const n = new Set(s); n.delete(chart.id); return n; });
            }
        };

        const tasks = [...uniqueByFunction.values()].map(
            ({ functionId, charts: chartsForFn }) => () => executeGroup(functionId, chartsForFn),
        );
        await runWithConcurrencyPool(tasks);
    }, [open, chartItems, clearChartState, debouncedSig, dashboard?.name, dispatch, registeredFunctions, t, applyResultToChart]);

    const runChart = useCallback(async (chartDef) => {
        const functionId = resolveChartFunctionId(chartDef, registeredFunctions, dashboard?.name);
        if (!functionId) return;
        setChartResults(s => ({ ...s, [chartDef.id]: null }));
        setChartErrors(s => ({ ...s, [chartDef.id]: null }));
        setLoadingIds(s => new Set([...s, chartDef.id]));

        const filter = buildChartExecuteFilter(chartDef, dateRangeRef.current, globalFiltersRef.current);
        const filterKey = buildChartFilterSignature(dateRangeRef.current, globalFiltersRef.current);
        const cacheKey = chartExecuteCacheKey(functionId, filterKey);
        let rawResult = sharedChartResultCache.get(cacheKey);
        if (!rawResult) {
            rawResult = await dispatch(executeReportFunction(functionId, filter));
            if (rawResult?.error) {
                setChartErrors(s => ({ ...s, [chartDef.id]: rawResult.error }));
                setChartResults(s => ({ ...s, [chartDef.id]: null }));
                setLoadingIds(s => { const n = new Set(s); n.delete(chartDef.id); return n; });
                return;
            }
            if (rawResult) sharedChartResultCache.set(cacheKey, rawResult);
        }

        const rawDataset = pickDatasetFromResult(rawResult, chartDef.datasetKey);
        if (rawDataset) {
            applyResultToChart(chartDef, rawDataset);
        } else {
            setChartErrors(s => ({ ...s, [chartDef.id]: t('Error loading chart') }));
            setChartResults(s => ({ ...s, [chartDef.id]: null }));
        }
        setLoadingIds(s => { const n = new Set(s); n.delete(chartDef.id); return n; });
    }, [dashboard?.name, dispatch, registeredFunctions, t, applyResultToChart]);

    useEffect(() => {
        localItemsRef.current = localItems;
    }, [localItems]);

    useEffect(() => () => {
        if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    }, []);

    useEffect(() => {
        if (!open) return;
        reloadAllCharts();
    }, [open, chartItemCount, debouncedSig, reloadAllCharts]);

    const openEditChart = useCallback((chartDef) => {
        setChartToEdit(chartDef);
        setChartDrawerOpen(true);
    }, []);

    const openAddChart = useCallback(() => {
        setChartToEdit(null);
        setChartDrawerOpen(true);
    }, []);

    const openAddText = useCallback(() => {
        setTextToEdit(null);
        setTextModalOpen(true);
    }, []);

    const openEditText = useCallback((item) => {
        setTextToEdit(item);
        setTextModalOpen(true);
    }, []);

    const handleSaveTextBlock = useCallback(({ title, description }) => {
        if (textToEdit) {
            setLocalItems(prev => prev.map(it => (
                it.id === textToEdit.id
                    ? { ...it, title, description }
                    : it
            )));
        } else {
            setLocalItems(prev => [...prev, createTextBlock({ title, description })]);
        }
        setIsDirty(true);
        setTextModalOpen(false);
        setTextToEdit(null);
    }, [textToEdit]);

    const handleSaveChart = useCallback((chartDef) => {
        setLocalItems(prev => {
            const exists = prev.some(c => c.id === chartDef.id);
            return exists ? prev.map(c => (c.id === chartDef.id ? chartDef : c)) : [...prev, chartDef];
        });
        setIsDirty(true);
        runChart(chartDef);
        setChartDrawerOpen(false);
        setChartToEdit(null);
    }, [runChart]);

    const handleRemoveItem = useCallback((itemId) => {
        setLocalItems(prev => prev.filter(c => c.id !== itemId));
        setIsDirty(true);
    }, []);

    const handleDuplicateChart = useCallback((chartDef) => {
        const copy = cloneChart(chartDef);
        setLocalItems(prev => [...prev, copy]);
        setIsDirty(true);
        runChart(copy);
    }, [runChart]);

    const handleDuplicateText = useCallback((item) => {
        setLocalItems(prev => [...prev, cloneTextBlock(item)]);
        setIsDirty(true);
    }, []);

    const persistLayoutItems = useCallback(async (items) => {
        const dashId = dashboard?.id ?? dashboard?.Id;
        if (!dashId) return;

        const { filters: _savedFilters, ...layoutRest } = dashboard.layoutJson ?? {};
        setLayoutSaving(true);
        const updated = await dispatch(patchReportDashboard(dashId, {
            name: (dashboardName.trim() || dashboard.name) ?? '',
            layoutJson: {
                ...layoutRest,
                charts: items,
            },
        }));
        setLayoutSaving(false);
        if (updated) {
            setIsDirty(false);
            onDashboardUpdated?.(updated);
        }
    }, [dashboard, dashboardName, dispatch, onDashboardUpdated]);

    const scheduleLayoutAutosave = useCallback((items) => {
        if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
        layoutSaveTimer.current = setTimeout(() => {
            persistLayoutItems(items);
        }, LAYOUT_AUTOSAVE_MS);
    }, [persistLayoutItems]);

    const handleLayoutChange = useCallback((newLayout) => {
        const currentGrid = localItemsRef.current.map(toGridLayoutItem);
        if (gridLayoutsEqual(newLayout, currentGrid)) return;

        const nextItems = mergeGridLayoutIntoItems(localItemsRef.current, newLayout);
        if (layoutItemsEqual(nextItems, localItemsRef.current)) return;

        localItemsRef.current = nextItems;
        setLocalItems(nextItems);
        if (skipLayoutAutosaveRef.current) return;
        setIsDirty(true);
        scheduleLayoutAutosave(nextItems);
    }, [scheduleLayoutAutosave]);

    useEffect(() => {
        if (!open || !dashboard) return;
        const items = parseLayoutItems(dashboard.layoutJson);
        setLocalItems(items);
        localItemsRef.current = items;
        setChartResults({});
        setChartErrors({});
        setIsDirty(false);
        skipLayoutAutosaveRef.current = true;
        const allowAutosave = setTimeout(() => {
            skipLayoutAutosaveRef.current = false;
        }, 150);
        return () => clearTimeout(allowAutosave);
    }, [open, dashboard?.id]);

    const commitDashboardName = useCallback(async () => {
        const dashId = dashboard?.id ?? dashboard?.Id;
        const trimmed = dashboardName.trim();
        if (!dashId || !trimmed || trimmed === (dashboard?.name ?? '')) return;
        const updated = await dispatch(patchReportDashboard(dashId, { name: trimmed }));
        if (updated) {
            onDashboardUpdated?.(updated);
        }
    }, [dashboard, dashboardName, dispatch, onDashboardUpdated]);

    const handleSave = async () => {
        const dashId = dashboard?.id ?? dashboard?.Id;
        if (!dashId) return;
        if (layoutSaveTimer.current) {
            clearTimeout(layoutSaveTimer.current);
            layoutSaveTimer.current = null;
        }
        const trimmedName = dashboardName.trim();
        if (!trimmedName) {
            message.error(t('Dashboard name is required'));
            return;
        }
        setSaving(true);
        const { filters: _savedFilters, ...layoutRest } = dashboard.layoutJson ?? {};
        const updated = await dispatch(patchReportDashboard(dashId, {
            name: trimmedName,
            layoutJson: {
                ...layoutRest,
                charts: localItems,
            },
        }));
        setSaving(false);
        if (updated) { setIsDirty(false); message.success(t('Dashboard saved')); }
    };

    const gridLayout = localItems.map(toGridLayoutItem);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="bottom"
            height="88vh"
            title={(
                <Space wrap style={{ maxWidth: 'min(520px, 70vw)' }}>
                    <Input
                        value={dashboardName}
                        onChange={e => { setDashboardName(e.target.value); setIsDirty(true); }}
                        onBlur={commitDashboardName}
                        onPressEnter={commitDashboardName}
                        placeholder={t('Dashboard name')}
                        style={{ width: 220, maxWidth: '50vw' }}
                    />
                    {(isDirty || layoutSaving) && (
                        <Badge
                            status="processing"
                            text={(
                                <Text style={{ fontSize: 12 }}>
                                    {layoutSaving ? t('Saving layout…') : t('Unsaved changes')}
                                </Text>
                            )}
                        />
                    )}
                </Space>
            )}
            extra={(
                <Space>
                    <Button
                        icon={<IconReload />}
                        disabled={chartItems.length === 0}
                        onClick={() => chartItems.forEach(runChart)}
                    >
                        {t('Reload charts')}
                    </Button>
                    <Button type="primary" icon={<SaveFilled />} loading={saving} onClick={handleSave}>
                        {t('Save dashboard')}
                    </Button>
                </Space>
            )}
            styles={{ body: { padding: 0, overflow: 'hidden', height: '100%' } }}
            destroyOnClose={false}
        >
            <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                {/* ── Left: Data Catalog ── */}
                <div style={{
                    width: SIDER_W,
                    flexShrink: 0,
                    borderRight: `1px solid ${token.colorBorderSecondary}`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        padding: '9px 14px',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillQuaternary,
                        flexShrink: 0,
                    }}>
                        <Text strong style={{ fontSize: 13 }}>{t('Data catalog')}</Text>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <DataCatalogSidebar
                            activeDsId={activeDsId}
                            setActiveDsId={setActiveDsId}
                            activeSchema={activeSchema}
                            setActiveSchema={setActiveSchema}
                        />
                    </div>
                </div>

                {/* ── Right: Chart grid ── */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div ref={containerRef} style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                        {localItems.length === 0 ? (
                            <AddLayoutPlaceholders
                                size="large"
                                onAddText={openAddText}
                                onAddChart={openAddChart}
                            />
                        ) : (
                            <>
                                <ResponsiveGrid
                                    className="layout"
                                    width={Math.max(containerWidth - 32, 400)}
                                    layouts={{ lg: gridLayout }}
                                    cols={COLS}
                                    rowHeight={30}
                                    {...gridLayoutDragProps(true)}
                                    onLayoutChange={handleLayoutChange}
                                    margin={[12, 12]}
                                >
                                    {localItems.map(item => {
                                        if (isTextLayoutItem(item)) {
                                            return (
                                                <div key={item.id}>
                                                    <ReportingTextBlockSection
                                                        item={item}
                                                        dragHandleClassName="drag-handle"
                                                        extra={(
                                                            <Space size={4}>
                                                                <Tooltip title={t('Edit')}>
                                                                    <Button size="small" icon={<IconEdit />} onClick={() => openEditText(item)} />
                                                                </Tooltip>
                                                                <Tooltip title={t('Duplicate')}>
                                                                    <Button size="small" icon={<CopyOutlined />} onClick={() => handleDuplicateText(item)} />
                                                                </Tooltip>
                                                                <Popconfirm
                                                                    title={t('Remove this section?')}
                                                                    onConfirm={() => handleRemoveItem(item.id)}
                                                                    okType="danger"
                                                                >
                                                                    <Button size="small" danger icon={<IconDelete />} />
                                                                </Popconfirm>
                                                            </Space>
                                                        )}
                                                    />
                                                </div>
                                            );
                                        }
                                        const loading = loadingIds.has(item.id);
                                        const dataset = loading ? null : chartResults[item.id];
                                        const vizSpec = enrichVizSpec(item.viz, item.dimensionFilter);
                                        const total = dataset && !isStatVizType(vizSpec)
                                            ? computeChartTotal(dataset, vizSpec)
                                            : null;
                                        return (
                                            <div key={item.id}>
                                                <ReportingChartCard
                                                    title={item.title || t('Chart')}
                                                    description={item.description}
                                                    dragHandleClassName="drag-handle"
                                                    extra={(
                                                        <>
                                                            {total != null && (
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    {t('Total')}: {total.toLocaleString()}
                                                                </Text>
                                                            )}
                                                            <Space size={4}>
                                                                <Tooltip title={t('Edit chart')}>
                                                                    <Button size="small" icon={<IconEdit />} onClick={() => openEditChart(item)} />
                                                                </Tooltip>
                                                                <Tooltip title={t('Duplicate chart')}>
                                                                    <Button size="small" icon={<CopyOutlined />} onClick={() => handleDuplicateChart(item)} />
                                                                </Tooltip>
                                                                <Tooltip title={t('Reload')}>
                                                                    <Button size="small" icon={<IconReload />} onClick={() => runChart(item)} />
                                                                </Tooltip>
                                                                <Popconfirm
                                                                    title={t('Remove this chart?')}
                                                                    onConfirm={() => handleRemoveItem(item.id)}
                                                                    okType="danger"
                                                                >
                                                                    <Button size="small" danger icon={<IconDelete />} />
                                                                </Popconfirm>
                                                            </Space>
                                                        </>
                                                    )}
                                                    chartProps={{
                                                        dataset,
                                                        vizSpec,
                                                        title: item.title,
                                                        defaultMode: item.viz?.mode,
                                                        loading,
                                                        error: chartErrors[item.id],
                                                        remountKey: filterSignature,
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </ResponsiveGrid>

                                <div style={{ marginTop: 12, paddingBottom: 16 }}>
                                    <AddLayoutPlaceholders
                                        size="large"
                                        onAddText={openAddText}
                                        onAddChart={openAddChart}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Nested Add Chart Drawer */}
            <AddChartDrawer
                open={chartDrawerOpen}
                onClose={() => { setChartDrawerOpen(false); setChartToEdit(null); }}
                onSave={handleSaveChart}
                chartToEdit={chartToEdit}
                dataSourceId={activeDsId}
                schema={activeSchema}
                dateRange={dateRange}
                globalFilters={globalFilters}
            />

            <TextBlockModal
                open={textModalOpen}
                onCancel={() => { setTextModalOpen(false); setTextToEdit(null); }}
                onSave={handleSaveTextBlock}
                initialValues={textToEdit}
            />
        </Drawer>
    );
}
